/**
 * @module CanvasReasoningPanelHeader
 * @purpose Reasoning panel header — title/count/completed labels, the FR2
 *   launch trigger, and the close button. Split out of CanvasReasoningPanel
 *   to keep that file under the 500-line budget.
 * @depends react-i18next, lucide-react, components/common (EvidenxSpinner)
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none — controlled entirely by caller props
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { X, RefreshCw } from 'lucide-react'
import { EvidenxSpinner } from '../../../../components/common'
import { formatRunTimestamp } from './CanvasReasoningPanelState'

const LOCKED_OPACITY = 0.5
const UNLOCKED_OPACITY = 1

export interface PanelHeaderProps {
  clusterCount: number | null
  /**
   * FR7 drop count. undefined = no run loaded yet (label hidden); null =
   * a run exists but the count was NOT MEASURED (predates this feature, or
   * never completed) — renders "not measured", never coalesced to 0; a
   * number (including 0) is a measured result.
   */
  unclusteredRowCount: number | null | undefined
  completedAt: string | null
  /** Run status. `completedAt` alone cannot say a run succeeded — the startup
   *  sweep stamps it when marking an interrupted run `failed`. */
  runStatus: string | undefined
  /** FR2: true while a launch is in flight, a run is active, the latest run
   *  is unknown (fetch failed), or the initial fetch is still loading. */
  launchLocked: boolean
  onForceRecompute: () => void
  onClose: () => void
}

function resolveUnclusteredRowLabel(
  t: TFunction<'analysis'>,
  unclusteredRowCount: number | null | undefined,
): string | null {
  if (unclusteredRowCount === undefined) return null
  if (unclusteredRowCount === null) return t('reasoning.panel.header.unclusteredRowCountNotMeasured')
  return t('reasoning.panel.header.unclusteredRowCount', { count: unclusteredRowCount })
}

interface PanelHeaderLabelsProps {
  clusterCount: number | null
  unclusteredRowCount: number | null | undefined
  completedAt: string | null
  runStatus: string | undefined
}

function PanelHeaderLabels({
  clusterCount,
  unclusteredRowCount,
  completedAt,
  runStatus,
}: PanelHeaderLabelsProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const countLabel =
    clusterCount === null
      ? null
      : t('reasoning.panel.header.clusterCount', { count: clusterCount })

  const unclusteredRowLabel = resolveUnclusteredRowLabel(t, unclusteredRowCount)

  const completedLabel =
    completedAt !== null && runStatus === 'completed'
      ? t('reasoning.panel.header.completedAt', { when: formatRunTimestamp(completedAt) })
      : null

  return (
    <div className="flex flex-col gap-0.5">
      <span
        className="text-sm font-semibold"
        style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}
      >
        {t('reasoning.panel.header.title')}
      </span>
      {countLabel !== null && (
        <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
          {countLabel}
        </span>
      )}
      {completedLabel !== null && (
        <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
          {completedLabel}
        </span>
      )}
      {unclusteredRowLabel !== null && (
        <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
          {unclusteredRowLabel}
        </span>
      )}
    </div>
  )
}

interface PanelHeaderTriggerProps {
  launchLocked: boolean
  onForceRecompute: () => void
}

/**
 * FR2: the header launch trigger. Disabled and spinner-labelled while any
 * lock term holds; the icon swap (RefreshCw <-> EvidenxSpinner) IS the busy
 * feedback for this control.
 */
function PanelHeaderTrigger({ launchLocked, onForceRecompute }: PanelHeaderTriggerProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <button
      type="button"
      onClick={onForceRecompute}
      disabled={launchLocked}
      aria-busy={launchLocked}
      aria-disabled={launchLocked}
      title={t('reasoning.panel.header.forceRecompute')}
      className="rounded p-1 transition-colors hover:bg-[var(--accent-dim)] disabled:cursor-not-allowed"
      style={{ color: 'var(--text-muted)', opacity: launchLocked ? LOCKED_OPACITY : UNLOCKED_OPACITY }}
      aria-label={t('reasoning.panel.header.forceRecompute')}
      data-testid="reasoning-panel-trigger"
    >
      {launchLocked ? (
        <EvidenxSpinner size="inline" label={t('common:busy.label', 'Working…')} />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" />
      )}
    </button>
  )
}

export function PanelHeader({
  clusterCount,
  unclusteredRowCount,
  completedAt,
  runStatus,
  launchLocked,
  onForceRecompute,
  onClose,
}: PanelHeaderProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div
      className="flex shrink-0 items-center justify-between border-b px-3 py-2"
      style={{ borderColor: 'var(--border)' }}
    >
      <PanelHeaderLabels
        clusterCount={clusterCount}
        unclusteredRowCount={unclusteredRowCount}
        completedAt={completedAt}
        runStatus={runStatus}
      />
      <div className="flex items-center gap-1">
        <PanelHeaderTrigger launchLocked={launchLocked} onForceRecompute={onForceRecompute} />
        <button
          type="button"
          onClick={onClose}
          title={t('reasoning.panel.header.close')}
          className="rounded p-1 transition-colors hover:bg-[var(--accent-dim)]"
          style={{ color: 'var(--text-muted)' }}
          aria-label={t('reasoning.panel.header.close')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
